import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useEffect, useRef } from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { useStarChat } from '../hooks/useStarChat';
import { useTabVisibility } from '../hooks/useTabVisibility';
import { Chat } from '../types';

import ChatTabActions from './ChatTabActions';
import RenameModal from './RenameModal';

interface ChatTabBarProps {
  chats: Chat[];
  activeChatId: string;
  onChatSelect: (chatId: string) => void;
  onCreateNewChat: () => void;
  onCloseTab?: (chatId: string) => void;
  isCreating?: boolean;
  error?: string | null;
}

const ChatTabBar: React.FC<ChatTabBarProps> = ({
  chats,
  activeChatId,
  onChatSelect,
  onCreateNewChat,
  onCloseTab,
  isCreating = false,
  error = null,
}) => {
  const { starChat, isStarring } = useStarChat();
  const { visibleTabs, hiddenTabs, tabContainerRef } = useTabVisibility(
    chats,
    activeChatId
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [renamingChat, setRenamingChat] = useState<Chat | null>(null);
  const [starringChatId, setStarringChatId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleStartRename = (chat: Chat) => {
    setRenamingChat(chat);
  };

  const handleToggleStar = async (chat: Chat) => {
    if (starringChatId !== chat.id && !isStarring) {
      setStarringChatId(chat.id);
      try {
        await starChat({ chatId: chat.id, starred: !chat.starred });
      } catch (error) {
        console.error('Star toggle failed:', error);
      } finally {
        setStarringChatId(null);
      }
    }
  };

  const handleCloseTab = (chat: Chat) => {
    if (onCloseTab) {
      onCloseTab(chat.id);
    }
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);

  if (error) {
    return (
      <div className='flex items-center justify-center bg-white border-b border-gray-200 px-4 py-8 shadow-sm'>
        <div className='text-center'>
          <div className='text-xl font-semibold text-red-600 mb-2'>
            Error loading conversations
          </div>
          <p className='text-red-500 mb-4 text-sm'>{error}</p>
          <button
            onClick={onCreateNewChat}
            disabled={isCreating}
            className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
              isCreating
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isCreating ? (
              <span className='flex items-center'>
                <LoadingSpinner size='md' color='border-white' className='mr-2' />
                Starting a new chat...
              </span>
            ) : (
              <span className='flex items-center'>
                <ChatBubbleLeftRightIcon className='w-5 h-5 mr-2' />
                Start a new chat
              </span>
            )}
          </button>
        </div>
      </div>
    );
  }

  if (chats.length === 0 && visibleTabs.length === 0) {
    return (
      <div className='flex items-center justify-center bg-white border-b border-gray-200 px-4 py-8 shadow-sm'>
        <div className='text-center'>
          <h2 className='text-xl font-semibold text-gray-800 mb-2'>
            Welcome to Acconto.ai
          </h2>
          <p className='text-gray-600 mb-4'>
            Start your new conversation below
          </p>
          <button
            onClick={onCreateNewChat}
            disabled={isCreating}
            className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
              isCreating
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isCreating ? (
              <span className='flex items-center'>
                <LoadingSpinner size='md' color='border-white' className='mr-2' />
                Starting a new chat...
              </span>
            ) : (
              <span className='flex items-center'>
                <ChatBubbleLeftRightIcon className='w-5 h-5 mr-2' />
                Start a new chat
              </span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center bg-white border-b border-gray-200 px-4 py-2 shadow-sm'>
      {/* Tabs Container */}
      <div className='flex-1 flex items-center min-w-0 mr-2'>
        <div
          ref={tabContainerRef}
          className='flex-1 flex items-center space-x-2 min-w-0'
        >
          {visibleTabs.map((chat: Chat) => (
            <div
              key={chat.id}
              className={`
                group flex items-center flex-shrink-0 rounded-lg text-sm font-medium transition-colors duration-200 border
                ${
                  chat.id === activeChatId
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent'
                }
              `}
              style={{ width: '220px' }}
            >
              <div className='flex-1 flex items-center min-w-0 group-hover:mr-2 transition-all duration-200'>
                <button
                  onClick={() => onChatSelect(chat.id)}
                  className='flex-1 px-3 py-2 truncate text-left'
                  title={chat.title}
                >
                  {chat.title}
                </button>
              </div>

              <div className='flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-0 group-hover:w-auto overflow-hidden'>
                <ChatTabActions
                  chat={chat}
                  isStarring={starringChatId === chat.id || isStarring}
                  onToggleStar={handleToggleStar}
                  onStartRename={handleStartRename}
                  onCloseTab={onCloseTab ? handleCloseTab : undefined}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Dropdown for Hidden Tabs */}
        {hiddenTabs.length > 0 && (
          <div className='relative ml-2' ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className='flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 border border-gray-200 transition-colors duration-200'
              aria-label={`Show ${hiddenTabs.length} more tabs`}
              title={`${hiddenTabs.length} more tabs`}
            >
              <ChevronDownIcon className='w-4 h-4 text-gray-600' />
            </button>

            {showDropdown && (
              <div className='absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[240px] max-h-[300px] overflow-y-auto'>
                {hiddenTabs.map((chat: Chat) => (
                  <div
                    key={chat.id}
                    className={`
                      group flex items-center justify-between px-4 py-2 text-sm font-medium transition-colors duration-200
                      ${
                        chat.id === activeChatId
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <button
                      onClick={() => {
                        onChatSelect(chat.id);
                        setShowDropdown(false);
                      }}
                      className='flex-1 text-left truncate'
                      title={chat.title}
                    >
                      {chat.title}
                    </button>

                    <div className='flex items-center space-x-1 ml-2'>
                      <ChatTabActions
                        chat={chat}
                        isStarring={starringChatId === chat.id || isStarring}
                        onToggleStar={handleToggleStar}
                        onStartRename={chat => {
                          handleStartRename(chat);
                          setShowDropdown(false);
                        }}
                        onCloseTab={onCloseTab ? handleCloseTab : undefined}
                        hoverClassName='hover:bg-gray-200'
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed New Chat Button */}
      <button
        onClick={onCreateNewChat}
        disabled={isCreating}
        className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
          isCreating
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200'
            : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
        }`}
        aria-label='Create new chat'
      >
        {isCreating ? (
          <span className='flex items-center'>
            <LoadingSpinner color='border-gray-500' className='mr-2' />
            Creating...
          </span>
        ) : (
          <span className='flex items-center'>
            <ChatBubbleLeftRightIcon className='w-4 h-4 mr-1' />
            New Chat
          </span>
        )}
      </button>

      {/* Rename Modal */}
      {renamingChat && (
        <RenameModal
          chat={renamingChat}
          onClose={() => setRenamingChat(null)}
        />
      )}
    </div>
  );
};

export default ChatTabBar;
